import { REGEX } from './regex';
import { UI } from './appConstants';

export { UI };

export const COLORS = {
  // Brand colors
  brand: {
    primaryDark: '#082B72',
    primaryDarkHover: '#062156',
    primaryDarkAlpha: '#082B7275',
    accent: '#023E8A',
    accentHover: '#012d6b'
  },

  // Section background colors
  section: {
    description: '#FEFCE8',
    acceptanceCriteria: '#FFF7EF',
    header: '#EFF4FF'
  },

  // UI element colors
  ui: {
    border: '#D9D9D9',
    buttonPaleBlue: '#C5D7FF',
    buttonPaleBlueHover: '#B2CAFF',
    avatarAmber: '#EAB308',
    commentActionBg: '#F0F0F0',
    commentActionHover: '#E0E0E0',
    divider: '#BDBDBD',
    lightDivider: '#f0f0f0',
    ghostBg: '#BBCFFE2B',
    ghostBorder: '#7A95D3b3',
    chipBg: '#DBE3FB',
    chipText: '#233057',
    menuHover: '#f9f9f9',
    snackbarBg: '#323232',
    grey999: '#999',
    greyEAEAEA: '#EAEAEA',
    greyE8E8E8: '#E8E8E8',
    greyC4C4C4: '#c4c4c4',
    greyE0E0E0: '#E0E0E0',
    greyF0F0F0: '#F0F0F0',
    orangeWarning: '#ff9800',
    greenSuccess: '#4caf50',
    brandPrimaryHover: '#0a3a9e',
    shadowLight: 'rgba(0,0,0,0.05)',
    shadowMedium: 'rgba(0,0,0,0.08)',
    shadowHeavy: '0 4px 12px rgba(0,0,0,0.15)',
    error8: 'rgba(211, 47, 47, 0.08)',
    white90: 'rgba(255,255,255,0.9)',
    blackShadow: 'rgba(0,0,0,0.15)',
    projectCardShadow: '#9EB3DB5E',
    projectCardBorderLight: '#B7CBF2',
    projectCardBorderDark: '#5374B7',
    shadow04: 'rgba(0,0,0,0.4)',
    shadow10: 'rgba(0,0,0,0.1)',
    shadow12: 'rgba(0,0,0,0.12)',
    primaryShadow: 'rgba(59, 130, 246, 0.25)',
    primaryShadowHover: 'rgba(59, 130, 246, 0.35)',
    notificationCheckboxBg: '#B7C8EB'
  },

  // Basic colors
  black: '#000000',
  pureBlack: '#000',
  darkGray: '#333',
  mediumGray: '#555',
  lightGray: '#aaa',
  transparent: 'transparent',

  // Character limit warning colors
  characterLimit: {
    background: '#fff3cd',
    border: '#ffeaa7',
    icon: '#f39c12',
    text: '#856404'
  },

  // Subtask specific colors
  subtask: {
    headerBg: '#E6EEFF',
    progressUnfilled: '#D6E3FF',
    progressFilled: '#A1BFFF',
    nestLevel0Bg: 'transparent',
    nestLevel1Bg: '#f1f5f9',
    nestLevel2Bg: '#e5e7eb',
    nestLevel3Bg: '#d1d5db',
    nestLevel0HoverBg: '#f9fafb',
    nestLevel1HoverBg: '#e5e7eb',
    nestLevel2HoverBg: '#d1d5db',
    nestLevel3HoverBg: '#cbd5e1'
  },

  primary: '#3b82f6',
  success: '#10b981',
  successDark: '#538D22',
  successLight: '#E9FED8',
  tableHeaderBg: '#F7F9FE',
  warning: '#f59e0b',
  error: '#ef4444',
  secondary: '#6b7280',
  background: '#f8fafc',
  white: '#ffffff',
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    muted: '#5A5A5A'
  },
  // Additional colors from components
  gradient: {
    primary: '#667eea',
    secondary: '#764ba2',
    primaryHover: '#5a6fd8',
    secondaryHover: '#6a4190'
  },
  alert: {
    error: '#dc2626'
  },
  projectAlerts: {
    projectHeaderBg: '#DBEAFE',
    accordionHeaderBg: '#F2F6FF'
  },
  disabled: {
    background: '#ccc',
    text: '#666'
  },
  border: '#e0e0e0',
  scrollbar: {
    track: '#f1f1f1',
    thumb: '#888',
    thumbHover: '#555',
    thumbLight: '#c1c1c1',
    thumbLightHover: '#a8a8a8'
  },
  highlight: UI.newTaskHighlight,
  lightBlueBg: '#F2F6FF',
  commentBox: {
    primary: '#0052cc',
    primaryHover: '#0747a6',
    activeBackground: '#deebff',
    toolbarBackground: '#f4f5f7',
    border: '#ddd',
    text: '#42526e',
    textDark: UI.screenshot.badgeBg,
    placeholder: '#6b778c',
    disabled: '#f4f5f7',
    disabledText: '#a5adba',
    codeBackground: '#f4f4f4',
    blockquoteBorder: '#ddd',
    blockquoteText: '#6b7280',
    // Used for clipboard image size validation error message
    imagePasteErrorText: '#c92a2a'
  },
  priority: {
    critical: '#dc2626',
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#10b981',
    unassigned: UI.unassignedPriorityColor
  },
  status: {
    open: '#64748b',
    inProgress: '#3b82f6',
    review: '#f59e0b',
    done: '#10b981',
    closed: '#10b981',
    blocked: '#ef4444',
    sprintCompleted: '#6FC812'
  },
  gray: {
    64: '#646464',
    400: '#94a3b8',
    500: '#6b7280',
    600: '#475569',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    300: '#d1d5db',
    200: '#e5e7eb',
    50: '#f9fafb',
    100: '#f1f5f9'
  },
  blue: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    bg: '#EFF6FF'
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    dark: '#c62828',
    light: '#ffcdd2',
    muiError: '#d32f2f',
    muiErrorHover: '#b71c1c',
    scrumWarning: '#D21724'
  },
  orange: {
    50: '#fff7ed',
    100: '#ffe0b2',
    500: '#f97316',
    600: '#a66200',
    700: '#c2410c'
  },
  pink: {
    500: '#ec4899'
  },
  indigo: {
    50: '#f5f7ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    500: '#6366f1'
  },
  slate: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    800: '#1e293b',
    900: '#0f172a'
  },
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    500: '#8b5cf6',
    600: '#7c3aed'
  },
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    500: '#10b981',
    600: '#059669'
  },
  cyan: {
    500: '#06b6d4'
  },

  // Timesheet specific colors
  timesheet: {
    filterGradientStart: '#3b82f6',
    filterGradientEnd: '#1d4ed8',
    filterGradientMulti: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4)',
    cardGradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    summaryGradient: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    shadowColor: 'rgba(0, 0, 0, 0.08)',
    shadowColorDark: 'rgba(0, 0, 0, 0.15)',
    filterIconShadow: 'rgba(59, 130, 246, 0.3)',
    primaryHover: '#1565c0',
    summaryBg: '#F2F6FF',
    summaryHover: '#e8efff',
    dateText: '#385795',
    rowBorder: '#F1F5F9',
    boxBorder: '#e0e7ff',
    boxHoverBg: '#f5f7ff',
    boxHoverBorder: '#c7d2fe',
    nestedHeaderBg: '#F8FAFC',
    nestedHeaderBorder: '#E2E8F0',
    fallbackColors: ['#FEF9C3', '#FFEDD5', '#DCFCE7', '#FEE2E2', '#E0F2FE', '#F3E8FF']
  },

  // AI Task Planner specific colors
  aiTaskPlanner: {
    failed: '#ef4444',
    inProgress: '#f59e0b',
    generating: '#f59e0b',
    approved: '#16a34a',
    completed: '#16a34a',
    complete: '#16a34a',
    progressIcon: '#92400e'
  },

  // AI Developer Settings specific colors
  aiDeveloperSettings: {
    benefitText: '#0369a1',
    errorText: '#ef4444'
  },

  // Calendar shared 100-color palette - medium vibrant colors only (no too light/dark)
  // Excluded colors: #3b82f6, #e2ecfe, #023E8A
  calendarPalette: [
    // Blues (10) - removed #3b82f6
    '#0ea5e9', '#06b6d4', '#0284c7', '#0369a1', '#1e40af', '#2563eb', '#60a5fa', '#38bdf8', '#0891b2', '#1d4ed8',
    // Greens (10)
    '#10b981', '#22c55e', '#84cc16', '#16a34a', '#059669', '#047857', '#14b8a6', '#4ade80', '#34d399', '#15803d',
    // Yellows/Oranges (10)
    '#f59e0b', '#eab308', '#f97316', '#fb923c', '#ea580c', '#d97706', '#b45309', '#fbbf24', '#f59e0b', '#fb923c',
    // Reds/Pinks (10)
    '#ef4444', '#f43f5e', '#ec4899', '#dc2626', '#be123c', '#991b1b', '#f87171', '#fb7185', '#db2777', '#be185d',
    // Purples (10)
    '#8b5cf6', '#a855f7', '#d946ef', '#7c3aed', '#9333ea', '#6d28d9', '#a78bfa', '#c084fc', '#e879f9', '#9f1239',
    // Teals/Cyans (10)
    '#14b8a6', '#2dd4bf', '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#5eead4', '#0d9488', '#0f766e', '#115e59',
    // Indigos (10)
    '#6366f1', '#818cf8', '#4f46e5', '#4338ca', '#3730a3', '#6366f1', '#4f46e5', '#5b21b6', '#7c3aed', '#6d28d9',
    // Limes/Emeralds (10)
    '#84cc16', '#a3e635', '#65a30d', '#4d7c0f', '#22c55e', '#16a34a', '#059669', '#10b981', '#14b8a6', '#0d9488',
    // Ambers/Golds (10)
    '#facc15', '#fbbf24', '#eab308', '#ca8a04', '#a16207', '#854d0e', '#f59e0b', '#d97706', '#b45309', '#92400e',
    // Mixed vibrant (10)
    '#f472b6', '#fb7185', '#fdba74', '#fcd34d', '#a78bfa', '#5eead4', '#f87171', '#fb923c', '#c084fc', '#ec4899',
    // Additional Blues (10) - removed #3b82f6
    '#2563eb', '#1d4ed8', '#60a5fa', '#0ea5e9', '#38bdf8', '#06b6d4', '#0891b2', '#0284c7', '#0369a1', '#1e40af',
    // Additional Greens (10)
    '#15803d', '#047857', '#10b981', '#34d399', '#22c55e', '#4ade80', '#84cc16', '#a3e635', '#65a30d', '#16a34a',
    // Additional Oranges (10)
    '#f97316', '#ea580c', '#c2410c', '#9a3412', '#fb923c', '#fdba74', '#fbbf24', '#f59e0b', '#eab308', '#d97706',
    // Additional Reds (10)
    '#dc2626', '#b91c1c', '#991b1b', '#f87171', '#ef4444', '#f43f5e', '#db2777', '#be185d', '#9f1239', '#be123c',
    // Additional Purples (10)
    '#7c3aed', '#6d28d9', '#5b21b6', '#a78bfa', '#8b5cf6', '#a855f7', '#d946ef', '#9333ea', '#c084fc', '#e879f9',
    // Additional Teals (10)
    '#0d9488', '#0f766e', '#115e59', '#14b8a6', '#2dd4bf', '#5eead4', '#06b6d4', '#0891b2', '#0e7490', '#155e75',
    // Additional Indigos (10)
    '#4338ca', '#3730a3', '#818cf8', '#6366f1', '#4f46e5', '#a5b4fc', '#5b21b6', '#7c3aed', '#6d28d9', '#9333ea',
    // Additional Limes (10)
    '#65a30d', '#4d7c0f', '#a3e635', '#84cc16', '#22c55e', '#16a34a', '#059669', '#047857', '#10b981', '#14b8a6',
    // Additional Yellows (10)
    '#ca8a04', '#a16207', '#854d0e', '#fbbf24', '#facc15', '#eab308', '#f59e0b', '#d97706', '#b45309', '#92400e',
    // Additional Pinks (10)
    '#db2777', '#be185d', '#9f1239', '#f9a8d4', '#f472b6', '#ec4899', '#fb7185', '#f43f5e', '#ef4444', '#dc2626'
  ],

  // Category-specific starting offsets for visual distinction
  calendarCategoryOffsets: {
    status: 0,      // Start from Blues (index 0)
    priority: 30,   // Start from Reds/Pinks (index 30)
    taskType: 10,   // Start from Greens (index 10)
    assignee: 40    // Start from Purples (index 40)
  },

  // Calendar task default colors
  calendarTask: {
    defaultBackground: '#e0f2fe',
    defaultBorder: '#3b82f6'
  },

  // Space management dashboard colors
  spaceDashboard: {
    cards: {
      spaces: {
        text: '#1FA8C8',
        background: '#E6F9FD',
        border: '#48CAE4',
        ring: '#21A9C4',
        shadow: 'rgba(33, 169, 196, 0.18)'
      },
      projects: {
        text: '#249000',
        background: '#EAF8E2',
        border: '#38B000',
        ring: '#60C900',
        shadow: 'rgba(96, 201, 0, 0.22)'
      },
      inactiveProjects: {
        text: '#B42318',
        background: '#FDEDEC',
        border: '#FDA29B',
        ring: '#F04438',
        shadow: 'rgba(240, 68, 56, 0.2)'
      },
      users: {
        text: '#D5A900',
        background: '#FFF9D8',
        border: '#FFD500',
        ring: '#FFCC00',
        shadow: 'rgba(213, 169, 0, 0.2)'
      }
    }
  },

  projectManagement: {
    membersModal: {
      headerAvatarBg: '#FF9800',
      leadBlue: '#315BFF',
      leadRowBg: '#EEF5FF',
      memberAvatarBg: '#05C986',
      leadChipBg: '#0973D8',
      scrumMasterChipBg: '#7C3AED',
      poChipBg: '#F97316',
      scrumMasterBorder: '#8B5CF6',
      poBorder: '#FB923C',
      scrumMasterRowBg: '#FAF5FF',
      poRowBg: '#FFF7ED',
      activeChipText: '#079346',
      activeChipBorder: '#16A34A',
      titleText: '#111827',
      sectionTitleText: '#1f2937',
      footerBg: '#f8fafc',
      closeButtonBorder: '#66a5ff',
      closeButtonText: '#006ee6',
      closeButtonHoverBg: '#eff6ff',
      memberCardShadow: 'rgba(0, 0, 0, 0.22)',
      scrollbarTrack: '#f1f5f9',
      scrollbarThumb: '#cbd5e1'
    }
  },

  // Board column background colors (keyed by status name keyword)
  boardColumn: {
    todo:       '#9CA3AF29',
    open:       '#9CA3AF29',
    inprogress: '#AACBFF2B',
    doing:      '#AACBFF2B',
    blocked:    '#FFD1D129',
    review:     '#F59E0B29',
    done:       '#6FC81229',
    closed:     '#6FC81229',
    complete:   '#6FC81229',
    assigned:   '#6366F129',
    hold:       '#A855F729',
    testing:    '#14B8A629',
    bug:        '#EF444429',
    enhancement:'#8B5CF629',
    backlog:    '#94A3B829',
    deployqa:   '#0EA5E929',
    readyqa:    '#0EA5E929',
    inqa:       '#0284C729',
    deployprod: '#DC262629',
    readyprod:  '#DC262629',
    inprod:     '#16A34A29',
    watch:      '#F9731629',
    default:    '#9CA3AF29'
  },
  charts: {
    idealLine: '#666666',
    actualGreen: '#6FC812',
    actualYellow: '#E7E027',
    actualRed: '#C51925',
    loadRed: '#C51925',
    loadOrange: '#ED7B1E',
    loadBlue: '#3B82F6',
    progressGreen: '#538D22'
  },
  shadows: {
    card: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    accordion: '0px 4px 10px rgba(0, 0, 0, 0.05)'
  },
  strategistAgent: {
    workflow: {
      requirement: { text: '#527eee', background: '#dbe7ff' },
      epic: { text: '#777', background: '#eeeeee' },
      feature: { text: '#7ca0d7', background: '#e8f1ff' },
      task: { text: '#63a82a', background: '#e8f5dd' },
      subtask: { text: '#7755ff', background: '#f0edff' }
    },
    projectBorder: '#86a4d9',
    projectHeader: '#f1f5ff',
    projectDivider: '#d8e2f4',
    activeBorder: '#52c41a',
    inactiveBorder: '#9aacc3',
    inactiveChipBackground: '#e4e7ec',
    inactiveChipText: '#40516d',
    warningBackground: '#fff1d6',
    warning: '#f59e0b',
    warningHover: '#d97706',
    deleteBackground: '#fff0f0',
    delete: '#e03131',
    deleteHover: '#c92a2a',
    deleteNameBorder: '#dbe3ee',
    deleteNameBackground: '#f8fafc',
    cancelBorder: '#d0d7e2'
  }
};

// Report chart colors
export const REPORT_CHART_COLORS = [
  '#082B72',
  '#00B4D8',
  '#6FC812',
  '#C7150E',
  '#F4A261',
  '#7B2CBF',
  '#2A9D8F',
  '#E76F51',
  '#4361EE',
  '#F9C74F',
  '#4D908E',
  '#F72585'
];

// Heatmap colors for visual intensity representation
export const heatmapColors = {
  0: '#cbd5e1', // Default for 0
  1: '#CAF0F8', 
  2: '#48CAE4', 
  3: '#0096C7', 
  4: '#0077B6', 
  5: '#03547E', 
  6: '#03045E', 
  max: '#03045E' 
};

// Common utility function for getting status colors
export const getStatusColor = (status) => {
  // Handle both string values and object IDs
  if (typeof status === 'object' && status && status.name) {
    status = status.name;
  }
  switch (status) {
    case 'To Do': return COLORS.slate[500];
    case 'In Progress': return COLORS.primary;
    case 'Review': return COLORS.warning;
    case 'Done': return COLORS.success;
    case 'Open': return COLORS.slate[500];
    case 'Closed': return COLORS.success;
    case 'Blocked': return COLORS.error;
    case 'On Hold': return COLORS.warning;
    case 'Pending': return COLORS.warning;
    case 'ON_TRACK':
    case 'On Track': return COLORS.primary;
    default: return COLORS.gray[500];
  }
};

// Returns background color string for a kanban column based on status name
export const getColumnTheme = (statusName = '') => {
  const name = statusName.toLowerCase();
  const map = COLORS.boardColumn;
  if (name.includes('todo') || name.includes('to do')) return map.todo;
  if (name.includes('progress') || name.includes('doing')) return map.inprogress;
  if (name.includes('blocked')) return map.blocked;
  if (name.includes('review')) return map.review;
  if (name.includes('done') || name.includes('closed') || name.includes('complete')) return map.done;
  if (name.includes('assigned')) return map.assigned;
  if (name.includes('hold')) return map.hold;
  if (name.includes('bug')) return map.bug;
  if (name.includes('enhancement')) return map.enhancement;
  if (name.includes('backlog')) return map.backlog;
  if (name.includes('deploy to prod') || name.includes('ready for prod') || name.includes('ready to prod')) return map.readyprod;
  if (name.includes('deploy to qa') || name.includes('ready for qa') || name.includes('ready to qa')) return map.readyqa;
  if (name.includes('in prod')) return map.inprod;
  if (name.includes('in qa')) return map.inqa;
  if (name.includes('prod')) return map.deployprod;
  if (name.includes('qa')) return map.deployqa;
  if (name.includes('watch')) return map.watch;
  if (name.includes('test')) return map.testing;
  if (name.includes('open')) return map.open;
  return map.default;
};

// Legacy color mappings for backward compatibility
export const LEGACY_COLORS = {
  '#6b7280': 'COLORS.gray[500]',
  '#e5e7eb': 'COLORS.gray[200]',
  '#10b981': 'COLORS.green[500]',
  '#8b5cf6': 'COLORS.purple[500]',
  '#3b82f6': 'COLORS.primary',
  '#111827': 'COLORS.gray[900]',
  '#f1f5f9': 'COLORS.slate[100]',
  '#e2e8f0': 'COLORS.slate[200]',
  '#64748b': 'COLORS.slate[500]',
  '#2563eb': 'COLORS.blue[600]',
  '#cbd5e1': 'COLORS.slate[300]',
  '#94a3b8': 'COLORS.slate[400]',
  '#f8fafc': 'COLORS.slate[50]',
  '#1f2937': 'COLORS.gray[800]',
  '#f0f9ff': 'COLORS.blue[50]',
  '#fef2f2': 'COLORS.red[50]',
  '#dc2626': 'COLORS.red[600]',
  '#475569': 'COLORS.gray[600]'
};

export const isValidHex = (hex) => REGEX.hexColor.test(hex);

export const hexToHsv = (hex) => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, v = max;

  let d = max - min;
  s = max === 0 ? 0 : d / max;

  if (max === min) {
    h = 0; // achromatic
  } else {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100)
  };
};

export const hsvToHex = (h, s, v) => {
  s /= 100;
  v /= 100;
  let r = 0, g = 0, b = 0;

  let i = Math.floor(h / 60);
  let f = h / 60 - i;
  let p = v * (1 - s);
  let q = v * (1 - f * s);
  let t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
    default: break;
  }

  const toHex = x => {
    const hexStr = Math.round(x * 255).toString(16);
    return hexStr.length === 1 ? '0' + hexStr : hexStr;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  if (hex.length !== 6) return { r: 255, g: 255, b: 255 };
  return {
    r: parseInt(hex.substring(0, 2), 16) || 0,
    g: parseInt(hex.substring(2, 4), 16) || 0,
    b: parseInt(hex.substring(4, 6), 16) || 0
  };
};

export const rgbToHex = (r, g, b) => {
  const toHex = x => {
    const val = Math.max(0, Math.min(255, x));
    const hexStr = val.toString(16);
    return hexStr.length === 1 ? '0' + hexStr : hexStr;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
